import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../context/LanguageContext';
import { 
  Award, 
  Users, 
  BookOpen, 
  Globe, 
  Heart, 
  Target,
  CheckCircle,
  Star
} from 'lucide-react';

const About = () => {
  const { t } = useLanguage();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const achievements = [
    { icon: Users, value: '500+', label: t('studentsGraduated') },
    { icon: Award, value: '30+', label: t('yearsOfExcellence') },
    { icon: BookOpen, value: '6', label: t('languagesOffered') },
    { icon: Globe, value: '25+', label: t('countriesRepresented') },
  ];

  const values = [
    {
      icon: Heart,
      title: t('title1about'),
      description: t('description1about')
    },
    {
      icon: Target,
      title: t('title2about'),
      description: t('description2about')
    },
    {
      icon: Globe,
      title: t('title3about'),
      description: t('description3about')
    },
    {
      icon: CheckCircle,
      title: t('title4about'),
      description: t('description4about')
    }
  ];

  const timeline = [
    {
      year: '2009',
      title: 'Foundation',
      description: 'Started with a dream to make language learning accessible and enjoyable for everyone.'
    },
    {
      year: '2012',
      title: 'First Certifications',
      description: 'Our first students achieved Cambridge English certifications, proving our methodology works.'
    },
    {
      year: '2015',
      title: 'Expansion',
      description: 'Added French, Spanish, and German programs due to high demand from our community.'
    },
    {
      year: '2018',
      title: 'Digital Innovation',
      description: 'Launched online learning platform, making our courses accessible worldwide.'
    },
    {
      year: '2020',
      title: 'Virtual Excellence',
      description: 'Seamlessly transitioned to hybrid learning, maintaining the same quality online.'
    },
    {
      year: '2023',
      title: 'Today',
      description: 'Leading language education provider with students from 25+ countries achieving their dreams.'
    }
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-br from-neutral-50 to-white">
      <div className="container mx-auto px-4 lg:px-6">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center px-6 py-3 rounded-full bg-primary-500/10 text-primary-500 mb-6">
            <Heart className="w-5 h-5 mr-2" />
            <span className="font-medium">{t('ourStory')}</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold font-heading text-primary-500 mb-6">
            {t('aboutTitle')}
            <span className="block text-accent-500">{t('aboutSubtitle')}</span>
          </h2>
          
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
            {t('aboutDescription')}
          </p>
        </motion.div>

        {/* Main Story Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            <h3 className="text-3xl font-bold text-primary-500 mb-6">
              {t('whereLanguage')}
            </h3>
            
            <div className="space-y-6 text-neutral-600 leading-relaxed">
              <p>
                {t('aboutp1')}
              </p>
              <p>
                {t('aboutp2')}
              </p>
              <p>
                {t('aboutp3')}
              </p>
              <p>
                {t('aboutp4')}
              </p>
              <p>
                {t('aboutp5')}
              </p>
              <p>
                {t('aboutp6')}
              </p>
            </div>

            <motion.div
              className="mt-8 flex flex-wrap gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <div className="flex items-center space-x-2 bg-white rounded-full px-4 py-2 shadow-md">
                <Award className="w-5 h-5 text-accent-500" />
                <span className="font-semibold text-primary-500">{t('certifiedInstructors')}</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.pexels.com/photos/31587201/pexels-photo-31587201.jpeg" 
                alt="Our learning environment"
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-primary-500/20 to-accent-500/20"></div>
            </div>
            
            {/* Floating Stats */}
            <motion.div
              className="absolute -bottom-8 -left-8 bg-white rounded-2xl p-6 shadow-xl"
              whileHover={{ scale: 1.05 }}
            >
              <div className="text-3xl font-bold text-primary-500">98%</div>
              <div className="text-sm text-neutral-600">{t('successRate')}</div>
            </motion.div>
            
            <motion.div
              className="absolute -top-8 -right-8 bg-accent-500 rounded-2xl p-6 shadow-xl text-white"
              whileHover={{ scale: 1.05 }}
            >
              <div className="text-3xl font-bold">30+</div>
              <div className="text-sm opacity-90">{t('yearsExperience')}</div>
            </motion.div>
          </motion.div>
        </div>

        {/* Achievements */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-20"
        >
          {achievements.map((achievement, index) => (
            <motion.div
              key={achievement.label}
              className="text-center"
              whileHover={{ y: -5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <achievement.icon className="w-8 h-8 text-white" />
              </div>
              <div className="text-3xl font-bold text-primary-500 mb-2">{achievement.value}</div>
              <div className="text-neutral-600">{achievement.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Values */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center text-primary-500 mb-12">{t('coreValues')}</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-neutral-100"
                whileHover={{ y: -5 }}
              >
                <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center mb-4">
                  <value.icon className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-xl font-bold text-primary-500 mb-3">{value.title}</h4>
                <p className="text-neutral-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, duration: 0.8 }}
        >
          <h3 className="text-3xl font-bold text-center text-primary-500 mb-12">Our Journey</h3>
          
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-primary-500 to-accent-500 hidden lg:block"></div>
            
            <div className="space-y-12">
              {timeline.map((event, index) => (
                <motion.div
                  key={event.year}
                  className="relative flex items-start space-x-8"
                  initial={{ opacity: 0, x: -50 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.8 + index * 0.1, duration: 0.6 }}
                >
                  {/* Timeline Dot */}
                  <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <span className="text-white font-bold text-sm">{event.year}</span>
                  </div>
                  
                  {/* Content */}
                  <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 flex-1 border border-neutral-100">
                    <h4 className="text-xl font-bold text-primary-500 mb-2">{event.title}</h4>
                    <p className="text-neutral-600">{event.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;